import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { brands, guides, safetyArticles } from '@/mocks/articles';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation('common');

  return (
    <footer className="bg-foreground-950 pt-14 md:pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Top Section */}
        <div className="flex flex-col md:flex-row items-start justify-between mb-12 md:mb-16 gap-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <span className="text-background-50 font-bold text-sm font-label">K</span>
            </div>
            <span className="text-background-50 font-heading font-semibold text-lg">Kakobuy Spreadsheet</span>
          </div>
          <p className="text-foreground-400 text-sm max-w-md font-body leading-relaxed">
            Niezależny przewodnik po arkuszach produktowych Kakobuy dla polskich użytkowników. Znajdź najlepsze linki i sprawdzone arkusze.
          </p>
        </div>

        {/* Middle Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-12 md:mb-16">
          {/* Categories */}
          <div>
            <h4 className="text-background-50 font-label font-semibold text-xs uppercase tracking-wider mb-4">
              {t('nav_categories')}
            </h4>
            <ul className="space-y-2.5">
              {categories.slice(0, 7).map((cat) => (
                <li key={cat.seq}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Brands */}
          <div>
            <h4 className="text-background-50 font-label font-semibold text-xs uppercase tracking-wider mb-4">
              Popularne marki
            </h4>
            <ul className="space-y-2.5">
              {brands.slice(0, 7).map((brand) => (
                <li key={brand.seq}>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                  >
                    {brand.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Guides & Safety */}
          <div>
            <h4 className="text-background-50 font-label font-semibold text-xs uppercase tracking-wider mb-4">
              Poradniki
            </h4>
            <ul className="space-y-2.5">
              {guides.map((guide) => (
                <li key={guide.seq}>
                  <Link
                    to={`/${guide.slug}`}
                    className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                  >
                    {guide.title}
                  </Link>
                </li>
              ))}
              {safetyArticles.map((article) => (
                <li key={article.seq}>
                  <Link
                    to={`/${article.slug}`}
                    className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                  >
                    {article.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About & Legal */}
          <div>
            <h4 className="text-background-50 font-label font-semibold text-xs uppercase tracking-wider mb-4">
              Informacje
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/czym-jest-kakobuy-spreadsheet"
                  className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                >
                  O nas
                </Link>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                >
                  {t('footer_privacy')}
                </a>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                >
                  {t('footer_terms')}
                </a>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                >
                  {t('footer_disclaimer')}
                </a>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-400 hover:text-background-50 text-sm font-body transition-colors duration-200 whitespace-nowrap"
                >
                  {t('footer_cookies')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Minimal%20product%20flat%20lay%20with%20folded%20cream%20linen%20shirt%20and%20leather%20watch%20strap%20on%20warm%20sand%20surface%2C%20soft%20natural%20side%20lighting%2C%20clean%20editorial%20photography&width=200&height=150&seq=footer-thumb-v2-2026&orientation=landscape"
                alt="Kakobuy Spreadsheet"
                title="Kakobuy Spreadsheet Polska"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-foreground-400 text-xs font-label leading-relaxed">
                &copy; 2026 Kakobuy Spreadsheet Polska
              </p>
              <p className="text-foreground-500 text-xs font-body mt-0.5">
                {t('all_rights_reserved')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-foreground-500 hover:text-background-50 transition-colors duration-200 text-sm cursor-pointer"
              aria-label="Twitter"
            >
              <i className="ri-twitter-x-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-foreground-500 hover:text-background-50 transition-colors duration-200 text-sm cursor-pointer"
              aria-label="Reddit"
            >
              <i className="ri-reddit-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-foreground-500 hover:text-background-50 transition-colors duration-200 text-sm cursor-pointer"
              aria-label="Medium"
            >
              <i className="ri-medium-line"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}